import React from 'react';
import api from '../api/axios'; // Adjust path as needed
import { Modal, Form, Input, Button, Typography, Row, Col } from 'antd';
import { useNavigate } from 'react-router-dom';
import { MailOutlined, LockOutlined, GoogleOutlined, WindowsOutlined } from '@ant-design/icons';

const { Title } = Typography;

const SignInModal = ({ visible, onClose, onLoginSuccess }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const handleSignUpRedirect = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling
    onClose();
    navigate('/signup');
  };

  const handleSubmit = (values) => {
    const loginData = {
      email: values.email,
      password: values.password,
    };

    api
      .post('/users/login', loginData)
      .then((response) => {
        console.log('User logged in successfully:', response.data);
        form.resetFields();

        const userRole = response.data.role?.toUpperCase();
        const redirectPaths = {
          STUDENT: '/student/dashboard',
          MENTOR: '/mentor/dashboard',
          VENDOR: '/vendor/dashboard',
          ADMIN: '/admin/dashboard',
        };
        const redirectPath = redirectPaths[userRole] || '/dashboard';

        if (onLoginSuccess) {
          onLoginSuccess(userRole);
        }

        Modal.success({
          title: 'Login Successful',
          content: `Welcome back! Redirecting to your ${userRole} dashboard...`,
          onOk: () => {
            onClose();
            navigate(redirectPath);
          },
          okText: 'Continue',
          afterClose: () => navigate(redirectPath),
          style: { borderRadius: '8px' },
          okButtonProps: {
            style: { background: '#13c2c2', borderColor: '#13c2c2', borderRadius: '4px' },
          },
        });
      })
      .catch((error) => {
        console.error('Error logging in:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
        });
        const errorMessage = error.response?.data?.message || error.message || 'Something went wrong!';
        Modal.error({
          title: 'Login Failed',
          content: errorMessage,
          style: { borderRadius: '8px' },
          okButtonProps: {
            style: { background: '#fa8c16', borderColor: '#fa8c16', borderRadius: '4px' },
          },
        });
      });
  };

  const handleSocialSignIn = (provider) => (e) => {
    e.stopPropagation(); // Prevent event bubbling
    console.log(`Sign in with ${provider}`);
    Modal.info({
      title: 'Coming Soon',
      content: `Sign in with ${provider} is not yet implemented.`,
      style: { borderRadius: '8px' },
      okButtonProps: {
        style: { background: '#13c2c2', borderColor: '#13c2c2', borderRadius: '4px' },
      },
    });
  };

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      style={{ minHeight: '450px', borderRadius: '12px', overflow: 'hidden' }}
      destroyOnClose
      bodyStyle={{ padding: 0 }}
    >
      <Row style={{ minHeight: '450px' }}>
        <Col
          xs={0}
          sm={12}
          style={{
            backgroundImage: 'url(/images/jecthublogin.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(19, 194, 194, 0.5), rgba(250, 140, 22, 0.5))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              textAlign: 'center',
              padding: '20px',
            }}
          >
            <Title level={2} style={{ color: '#fff', fontWeight: '700', margin: 0 }}>
              Welcome to JectHub
            </Title>
          </div>
        </Col>
        <Col xs={24} sm={12}>
          <Form
            form={form}
            style={{
              padding: '32px',
              height: '100%',
              background: '#fff',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            validateMessages={{
              required: '${label} is required!',
              types: { email: 'Please enter a valid email!' },
            }}
            onFinish={handleSubmit}
          >
            <Title
              level={4}
              style={{
                background: 'linear-gradient(45deg, #13c2c2, #08979c)',
                color: '#fff',
                textAlign: 'center',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '24px',
                fontWeight: '700',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              }}
            >
              Sign In
            </Title>

            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Please enter your email!' },
                { type: 'email', message: 'Please enter a valid email!' },
              ]}
            >
              <Input
                prefix={<MailOutlined style={{ color: '#13c2c2' }} />}
                placeholder="Enter your email address"
                size="large"
                style={{ borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
              />
            </Form.Item>

            <Form.Item
              label="Password"
              name="password"
              rules={[{ required: true, message: 'Please enter your password!' }]}
              style={{ marginBottom: '24px' }}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#13c2c2' }} />}
                placeholder="Enter your password"
                size="large"
                style={{ borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                style={{
                  background: 'linear-gradient(45deg, #fa8c16, #d46b08)',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  height: '48px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(45deg, #d46b08, #ad4e00)';
                  e.target.style.boxShadow = '0 6px 12px rgba(0,0,0,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'linear-gradient(45deg, #fa8c16, #d46b08)';
                  e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                }}
              >
                Sign In
              </Button>
            </Form.Item>

            <div style={{ textAlign: 'center', margin: '20px 0', position: 'relative' }}>
              <div style={{ borderBottom: '1px solid #dcdcdc' }} />
              <span
                style={{
                  background: '#fff',
                  padding: '0 12px',
                  position: 'absolute',
                  top: '-10px',
                  color: '#888',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                OR
              </span>
            </div>

            <Row gutter={16}>
              <Col span={12}>
                <Button
                  size="large"
                  block
                  style={{
                    background: 'linear-gradient(45deg, #4285F4, #357ABD)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    height: '44px',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                    transition: 'all 0.3s ease',
                  }}
                  onClick={handleSocialSignIn('Google')}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'linear-gradient(45deg, #357ABD, #2A62A0)';
                    e.target.style.boxShadow = '0 6px 12px rgba(0,0,0,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'linear-gradient(45deg, #4285F4, #357ABD)';
                    e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                  }}
                >
                  <GoogleOutlined /> Google
                </Button>
              </Col>
              <Col span={12}>
                <Button
                  size="large"
                  block
                  style={{
                    background: 'linear-gradient(45deg, #00A4EF, #0086C9)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    height: '44px',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                    transition: 'all 0.3s ease',
                  }}
                  onClick={handleSocialSignIn('Microsoft')}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'linear-gradient(45deg, #0086C9, #0068A3)';
                    e.target.style.boxShadow = '0 6px 12px rgba(0,0,0,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'linear-gradient(45deg, #00A4EF, #0086C9)';
                    e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                  }}
                >
                  <WindowsOutlined /> Microsoft
                </Button>
              </Col>
            </Row>

            <Form.Item style={{ textAlign: 'center', marginTop: '20px' }}>
              <span style={{ color: '#595959', fontSize: '14px' }}>
                Don’t have an account?{' '}
                <a
                  href="#"
                  onClick={handleSignUpRedirect}
                  style={{ color: '#13c2c2', fontWeight: 'bold', transition: 'color 0.3s ease' }}
                  onMouseEnter={(e) => (e.target.style.color = '#08979c')}
                  onMouseLeave={(e) => (e.target.style.color = '#13c2c2')}
                >
                  Sign Up
                </a>
              </span>
            </Form.Item>
          </Form>
        </Col>
      </Row>

      {/* Custom CSS */}
      <style jsx global>{`
        .ant-modal {
          animation: slideIn 0.3s ease-in-out;
        }
        .ant-form-item-label > label {
          color: #595959;
          font-weight: 500;
        }
        .ant-input-affix-wrapper, .ant-input-password {
          transition: all 0.3s ease;
        }
        .ant-input-affix-wrapper:hover, .ant-input-password:hover {
          border-color: #13c2c2 !important;
          box-shadow: 0 4px 8px rgba(0,0,0,0.15) !important;
        }
        .ant-input-affix-wrapper-focused, .ant-input-password-focused {
          border-color: #fa8c16 !important;
          box-shadow: 0 4px 8px rgba(0,0,0,0.15) !important;
        }
        @keyframes slideIn {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Modal>
  );
};

export default SignInModal;